import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog.tsx"
import { Button } from "../ui/button.tsx"
import { Input } from "../ui/input.tsx"
import { Label } from "../ui/label.tsx"

export function LoginModal({ isOpen, onClose, onLoginSuccess }) {
  const [step, setStep] = useState("phone")
  const [mode, setMode] = useState("login") // "login" or "signup"
  const [phone, setPhone] = useState("")
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)
  const [registrationData, setRegistrationData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    gender: "",
    email: "",
    address: "",
    city: ""
  })

  const handleSendOTP = async (e) => {
    e.preventDefault()
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      setStep("otp")
    }, 1000)
  }

  const handleVerifyOTP = async (e) => {
    e.preventDefault()
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      if (mode === "signup") {
        setStep("registration")
      } else {
        const userData = { name: "User", phone: phone }
        localStorage.setItem("user", JSON.stringify(userData))
        onLoginSuccess(userData)
        onClose()
        resetForm()
      }
    }, 1000)
  }

  const handleRegistration = async (e) => {
    e.preventDefault()
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      const userData = { 
        name: `${registrationData.firstName} ${registrationData.lastName}`, 
        phone: phone,
        ...registrationData
      }
      localStorage.setItem("user", JSON.stringify(userData))
      onLoginSuccess(userData)
      onClose()
      resetForm()
    }, 1000)
  }

  const resetForm = () => {
    setStep("phone")
    setMode("login")
    setPhone("")
    setOtp("")
    setRegistrationData({
      firstName: "",
      lastName: "",
      dob: "",
      gender: "",
      email: "",
      address: "",
      city: ""
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={step === "registration" ? undefined : onClose}>
      <DialogContent className="w-[90vw] sm:w-[95vw] max-w-[450px] p-4 sm:p-6 max-h-[90vh] overflow-y-auto" hideClose={step === "registration"} onInteractOutside={(e) => step === "registration" && e.preventDefault()}>
        <div className="flex flex-col items-center">
          <img src="/images/pranaam-logo.png" alt="Pranaam 365" className="h-16 sm:h-24 mb-3 sm:mb-4" />
          <DialogHeader className="text-center w-full">
            <DialogTitle className="text-xl sm:text-1xl font-bold mb-1 text-center">
              {step === "phone" ? (mode === "login" ? "Login" : "Signup") : step === "otp" ? "Verify OTP" : "Registration"}
            </DialogTitle>
            <p className="text-gray-500 text-xs sm:text-sm text-center">
              {step === "phone" 
                ? `Enter your phone number to receive an OTP for ${mode === "login" ? "login" : "signup"}.` 
                : step === "otp"
                ? `We've sent a verification code to +91 ${phone}`
                : "Complete your registration"}
            </p>
          </DialogHeader>
        </div>
        
        {step === "phone" ? (
          <form onSubmit={handleSendOTP} className="space-y-3 mt-3 sm:mt-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-xs sm:text-sm font-medium">Phone Number</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-900 font-medium text-xs sm:text-sm">+91</span>
                <Input 
                  id="phone" 
                  type="tel" 
                  placeholder="Enter your phone number" 
                  value={phone} 
                  onChange={(e) => setPhone(e.target.value)} 
                  required 
                  maxLength={10} 
                  className="text-xs sm:text-sm py-4 sm:py-5 pl-10 sm:pl-12" 
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-red-700 hover:bg-red-800 py-4 sm:py-5 text-xs sm:text-sm" disabled={loading}>
              {loading ? "Sending..." : "Send OTP"}
            </Button>
            {mode === "login" ? (
              <p className="text-center text-xs sm:text-sm text-gray-600">
                Don't have an account? <button type="button" onClick={() => setMode("signup")} className="text-red-700 font-medium">Register</button>
              </p>
            ) : (
              <p className="text-center text-xs sm:text-sm">
                <button type="button" onClick={() => setMode("login")} className="text-red-700 font-medium">Login</button>
              </p>
            )}
          </form>
        ) : step === "otp" ? (
          <form onSubmit={handleVerifyOTP} className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <div className="space-y-3 sm:space-y-4">
              <Label className="text-xs sm:text-sm font-medium text-center block">Enter verification code</Label>
              <div className="flex justify-center gap-1.5 sm:gap-2">
                {[...Array(6)].map((_, i) => (
                  <Input
                    key={i}
                    type="text"
                    maxLength={1}
                    className="w-8 h-8 sm:w-12 sm:h-12 text-center text-base sm:text-lg border-2 rounded-lg"
                    value={otp[i] || ''}
                    onChange={(e) => {
                      const newOtp = otp.split('')
                      newOtp[i] = e.target.value
                      setOtp(newOtp.join(''))
                      if (e.target.value && i < 5) {
                        const nextInput = e.target.parentElement?.children[i + 1]
                        nextInput?.focus()
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !otp[i] && i > 0) {
                        const prevInput = e.target.parentElement?.children[i - 1]
                        prevInput?.focus()
                      }
                    }}
                  />
                ))}
              </div>
            </div>
            <p className="text-center text-xs sm:text-sm text-gray-500">Resend code in 27s</p>
            <Button type="submit" className="w-full bg-red-700 hover:bg-red-800 py-4 sm:py-5 text-xs sm:text-sm" disabled={loading}>
              {loading ? "Verifying..." : "Verify & Continue"}
            </Button>
            <button type="button" onClick={() => setStep("phone")} className="w-full text-center text-red-700 font-medium text-xs sm:text-sm">
              Back to Login
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegistration} className="space-y-3 mt-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-xs font-medium">First Name</Label>
                <Input 
                  id="firstName" 
                  placeholder="First name" 
                  value={registrationData.firstName} 
                  onChange={(e) => setRegistrationData({...registrationData, firstName: e.target.value})} 
                  required 
                  className="text-xs py-4"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-xs font-medium">Last Name</Label>
                <Input 
                  id="lastName" 
                  placeholder="Last name" 
                  value={registrationData.lastName} 
                  onChange={(e) => setRegistrationData({...registrationData, lastName: e.target.value})} 
                  required 
                  className="text-xs py-4"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="dob" className="text-xs font-medium">Date of birth</Label>
                <Input 
                  id="dob" 
                  type="date" 
                  value={registrationData.dob} 
                  onChange={(e) => setRegistrationData({...registrationData, dob: e.target.value})} 
                  required 
                  className="text-xs py-4"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender" className="text-xs font-medium">Gender</Label>
                <select 
                  id="gender" 
                  value={registrationData.gender} 
                  onChange={(e) => setRegistrationData({...registrationData, gender: e.target.value})} 
                  required 
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-red-700"
                >
                  <option value="">Choose Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-medium">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="Email" 
                  value={registrationData.email} 
                  onChange={(e) => setRegistrationData({...registrationData, email: e.target.value})} 
                  required 
                  className="text-xs py-4"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city" className="text-xs font-medium">City</Label>
                <Input 
                  id="city" 
                  placeholder="City" 
                  value={registrationData.city} 
                  onChange={(e) => setRegistrationData({...registrationData, city: e.target.value})} 
                  required 
                  className="text-xs py-4"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address" className="text-xs font-medium">Address</Label>
              <textarea 
                id="address" 
                placeholder="Address" 
                value={registrationData.address} 
                onChange={(e) => setRegistrationData({...registrationData, address: e.target.value})} 
                required 
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-red-700"
              />
            </div>
            <Button type="submit" className="w-full bg-red-700 hover:bg-red-800 py-4 text-xs" disabled={loading}>
              {loading ? "Registering..." : "Register"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
